import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { astraAPI } from '../../../services/astraApi';

/**
 * Strategy Performance Table Component
 *
 * 🔧 PRODUCTION VERSION (2025-10-27): Real Strategy Data
 *
 * Issues Fixed:
 * 1. Removed 100% hardcoded strategies array (6 fake strategies)
 * 2. Integrated with /trading/strategies/performance API
 * 3. Added loading and error states
 * 4. Dynamic sorting and filtering
 * 5. Real performance metrics from database
 */

const StrategyPerformanceTable = () => {
  const [sortConfig, setSortConfig] = useState({ key: 'returns', direction: 'desc' });
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStrategyPerformance();

    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchStrategyPerformance, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchStrategyPerformance = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch real strategy performance from API
      const data = await astraAPI.getStrategyPerformance();

      console.log('📊 Strategy Performance Data:', {
        strategiesCount: data.length,
        sample: data.slice(0, 2)
      });

      setStrategies(data);

    } catch (err) {
      console.error('Failed to fetch strategy performance:', err);
      setError('Failed to load strategy performance');
      setStrategies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const handleSelectStrategy = (strategyId) => {
    setSelectedStrategies((prev) =>
      prev.includes(strategyId)
        ? prev.filter((id) => id !== strategyId)
        : [...prev, strategyId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStrategies.length === strategies.length) {
      setSelectedStrategies([]);
    } else {
      setSelectedStrategies(strategies.map((s) => s.id));
    }
  };

  const sortedStrategies = [...strategies].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' ? (
      <Icon name="ArrowUp" size={14} className="text-primary" />
    ) : (
      <Icon name="ArrowDown" size={14} className="text-primary" />
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'paused':
        return 'bg-yellow-100 text-yellow-700';
      case 'stopped':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Strategy Performance</h3>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading strategies...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Strategy Performance</h3>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="AlertTriangle" size={24} className="text-error mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">{error}</p>
            <button
              onClick={fetchStrategyPerformance}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (strategies.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Strategy Performance</h3>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="Layers" size={24} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No strategies active yet</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Layers" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Strategy Performance</h3>
            <p className="text-sm text-muted-foreground">
              {strategies.length} active strategies • {selectedStrategies.length} selected
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStrategyPerformance}
            iconName="RefreshCw"
            iconSize={14}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3">
                <input
                  type="checkbox"
                  checked={selectedStrategies.length === strategies.length && strategies.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-3">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Strategy</span>
                  <SortIcon columnKey="name" />
                </button>
              </th>
              <th className="text-left p-3">
                <span className="text-sm font-medium text-muted-foreground">Status</span>
              </th>
              <th className="text-right p-3">
                <button
                  onClick={() => handleSort('returns')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground ml-auto"
                >
                  <span>Returns</span>
                  <SortIcon columnKey="returns" />
                </button>
              </th>
              <th className="text-right p-3">
                <button
                  onClick={() => handleSort('sharpe_ratio')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground ml-auto"
                >
                  <span>Sharpe</span>
                  <SortIcon columnKey="sharpe_ratio" />
                </button>
              </th>
              <th className="text-right p-3">
                <button
                  onClick={() => handleSort('max_drawdown')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground ml-auto"
                >
                  <span>Max DD</span>
                  <SortIcon columnKey="max_drawdown" />
                </button>
              </th>
              <th className="text-right p-3">
                <button
                  onClick={() => handleSort('win_rate')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground ml-auto"
                >
                  <span>Win Rate</span>
                  <SortIcon columnKey="win_rate" />
                </button>
              </th>
              <th className="text-right p-3">
                <button
                  onClick={() => handleSort('total_trades')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground ml-auto"
                >
                  <span>Trades</span>
                  <SortIcon columnKey="total_trades" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedStrategies.map((strategy) => (
              <tr
                key={strategy.id}
                className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedStrategies.includes(strategy.id)}
                    onChange={() => handleSelectStrategy(strategy.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-3">
                  <div>
                    <div className="text-sm font-medium text-foreground">{strategy.name}</div>
                    <div className="text-xs text-muted-foreground">{strategy.id}</div>
                  </div>
                </td>
                <td className="p-3">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      strategy.status
                    )}`}
                  >
                    {strategy.status}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <span className={`text-sm font-medium ${strategy.returns >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {strategy.returns >= 0 ? '+' : ''}₹{strategy.returns.toLocaleString('en-IN')}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <span className="text-sm text-foreground">{strategy.sharpe_ratio?.toFixed(2) || '0.00'}</span>
                </td>
                <td className="p-3 text-right">
                  <span className="text-sm text-red-600">{strategy.max_drawdown?.toFixed(1) || '0.0'}%</span>
                </td>
                <td className="p-3 text-right">
                  <span className="text-sm text-foreground">{strategy.win_rate?.toFixed(1) || '0.0'}%</span>
                </td>
                <td className="p-3 text-right">
                  <span className="text-sm text-foreground">{strategy.total_trades || 0}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-foreground">
              {strategies.length}
            </div>
            <div className="text-xs text-muted-foreground">Total Strategies</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">
              {strategies.filter(s => s.status === 'active').length}
            </div>
            <div className="text-xs text-muted-foreground">Active</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">
              {strategies.reduce((sum, s) => sum + (s.total_trades || 0), 0)}
            </div>
            <div className="text-xs text-muted-foreground">Total Trades</div>
          </div>
          <div>
            <div className={`text-2xl font-bold ${
              strategies.reduce((sum, s) => sum + (s.returns || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              ₹{strategies.reduce((sum, s) => sum + (s.returns || 0), 0).toLocaleString('en-IN')}
            </div>
            <div className="text-xs text-muted-foreground">Combined P&L</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyPerformanceTable;
